import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { IncidentType } from '@parking/shared';
import { api } from '../shared/api/client';
import { useAuthStore } from '../shared/store/authStore';
import { enumLabel } from '../shared/i18n/enumLabel';
import { PageHeader } from '../shared/ui/PageHeader';
import { Card } from '../shared/ui/Card';
import { ParkingDataTable } from '../shared/ui/ParkingDataTable';
import { AppModal } from '../shared/ui/AppModal';
import { StatusBadge, statusToneFromEnum } from '../shared/ui/StatusBadge';

const TYPES: IncidentType[] = [
  'BROKEN_VEHICLE',
  'BLOCKED_SPOT',
  'BARRIER_FAULT',
  'CONFLICT',
  'LOST_PROPERTY',
  'DAMAGE',
  'OTHER',
];

export function IncidentsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const canRead = useAuthStore((s) => s.hasPermission('incidents:read'));
  const canWrite = useAuthStore((s) => s.hasPermission('incidents:write'));
  const list = useQuery({
    queryKey: ['incidents'],
    queryFn: () => api.listIncidents(),
    enabled: canRead,
  });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: 'OTHER' as IncidentType, title: '', description: '' });

  const create = useMutation({
    mutationFn: () =>
      api.createIncident({ type: form.type, title: form.title, description: form.description || null }),
    onSuccess: async () => {
      setOpen(false);
      await qc.invalidateQueries({ queryKey: ['incidents'] });
    },
  });
  const resolve = useMutation({
    mutationFn: (id: string) => api.resolveIncident(id),
    onSuccess: async () => qc.invalidateQueries({ queryKey: ['incidents'] }),
  });

  if (!canRead) {
    return <div className="alert alert-warning">{t('incidents.forbidden')}</div>;
  }

  const rows = list.data?.data ?? [];
  const columns = [
    t('incidents.type'),
    t('incidents.titleCol'),
    t('incidents.status'),
    t('incidents.created'),
    ...(canWrite ? [t('common.actions')] : []),
  ];

  return (
    <>
      <PageHeader
        actions={
          canWrite ? (
            <button type="button" className="btn btn-primary" onClick={() => setOpen(true)}>
              {t('incidents.create')}
            </button>
          ) : undefined
        }
      />

      <Card>
        <ParkingDataTable
          storageKey="incidents"
          columns={columns}
          refreshKey={rows.map((i) => `${i.id}:${i.status}`).join('|')}
          actionsColumn={canWrite}
          galleryItems={rows.map((i) => ({
            id: i.id,
            icon: 'bi-exclamation-triangle',
            iconTone: statusToneFromEnum(i.status),
            title: i.title,
            subtitle: enumLabel(t, 'incidentType', i.type),
            meta: new Date(i.createdAt).toLocaleString(),
            badge: (
              <StatusBadge tone={statusToneFromEnum(i.status)}>
                {enumLabel(t, 'incidentStatus', i.status)}
              </StatusBadge>
            ),
            actions:
              canWrite && i.status !== 'RESOLVED' && i.status !== 'CLOSED' ? (
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={() => resolve.mutate(i.id)}
                >
                  {t('incidents.resolve')}
                </button>
              ) : undefined,
          }))}
        >
          {rows.map((i) => (
            <tr key={i.id}>
              <td>{enumLabel(t, 'incidentType', i.type)}</td>
              <td className="fw-semibold">{i.title}</td>
              <td>
                <StatusBadge tone={statusToneFromEnum(i.status)}>
                  {enumLabel(t, 'incidentStatus', i.status)}
                </StatusBadge>
              </td>
              <td>{new Date(i.createdAt).toLocaleString()}</td>
              {canWrite && (
                <td>
                  {i.status !== 'RESOLVED' && i.status !== 'CLOSED' && (
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={() => resolve.mutate(i.id)}
                    >
                      {t('incidents.resolve')}
                    </button>
                  )}
                </td>
              )}
            </tr>
          ))}
        </ParkingDataTable>
      </Card>

      <AppModal
        open={open}
        onClose={() => setOpen(false)}
        title={t('incidents.create')}
        footer={
          <>
            <button type="button" className="btn btn-light" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!form.title || create.isPending}
              onClick={() => create.mutate()}
            >
              {t('common.save')}
            </button>
          </>
        }
      >
        <div className="d-grid gap-3">
          <div>
            <label className="form-label">{t('incidents.type')}</label>
            <select
              className="form-select"
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value as IncidentType })}
            >
              {TYPES.map((ty) => (
                <option key={ty} value={ty}>
                  {enumLabel(t, 'incidentType', ty)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label">{t('incidents.titleCol')}</label>
            <input
              className="form-control"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
          </div>
          <div>
            <label className="form-label">{t('incidents.description')}</label>
            <textarea
              className="form-control"
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>
        </div>
      </AppModal>
    </>
  );
}
